import { LeftOutlined } from '@ant-design/icons'
import { useQueryClient } from '@tanstack/react-query'
import { Button, Card, Flex, Form, Segmented } from 'antd'
import { useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import {
  useQuestionnairesServiceGetQuestionnaireEndpoint,
  useQuestionnairesServiceGetQuestionnaireEndpointKey,
  useQuestionnairesServiceUpdateQuestionnaireEndpoint
} from 'src/api/queries'
import {
  ApiError,
  QuestionnaireState,
  UpdateAnswerChoiceDto,
  UpdateQuestionDto
} from 'src/api/requests'
import { LoadingScreen, notification, PageBreadcrumbs } from 'src/components'
import { useLocales } from 'src/locales'
import { paths } from 'src/paths'
import { formErrorHandler } from 'src/utils'

import {
  QuestionnairesQuestions,
  QuestionnairesStats,
  Preview
} from '../components'
import { useStyles } from '../styles'

const QuestionnaireEnum = {
  questions: 'questions',
  answers: 'answers'
}

export const EditQuestionnaires = () => {
  const [type, setType] = useState(QuestionnaireEnum.questions)
  const [form] = Form.useForm()

  const [isPreview, setIsPreview] = useState(false)
  const { id } = useParams()

  const navigate = useNavigate()
  const { t } = useLocales()
  const { styles } = useStyles()

  const { data, isLoading } = useQuestionnairesServiceGetQuestionnaireEndpoint(
    {
      id: String(id)
    },
    [String(id)]
  )

  const segmentedItems = [
    {
      value: QuestionnaireEnum.questions,
      label: t('questionnaires.questions'),
      children: <QuestionnairesQuestions data={data} form={form} />
    },
    {
      value: QuestionnaireEnum.answers,
      label: t('questionnaires.answers'),
      children: <QuestionnairesStats data={data} />
    }
  ]

  const isDraft = data?.state === QuestionnaireState.DRAFT

  const onToggle = () => {
    setIsPreview((prev) => !prev)
  }

  const onTabChange = (val: string) => {
    setType(val)
    setIsPreview(false)
  }

  const queryClient = useQueryClient()

  const { mutate } = useQuestionnairesServiceUpdateQuestionnaireEndpoint({
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [
          useQuestionnairesServiceGetQuestionnaireEndpointKey,
          String(id)
        ]
      })
      notification.success({
        message: t('common.save_success')
      })
    },
    onError: (e: ApiError) => formErrorHandler(e.body.errors)
  })
  const onSubmit = async () => {
    const values = await form.validateFields()

    mutate({
      id: String(id),
      requestBody: {
        name: {
          ro: values.roTitle,
          ru: values.ruTitle
        },
        description: {
          ro: values.roDescription,
          ru: values.ruDescription
        },
        questions: values?.questions?.map(
          (item: UpdateQuestionDto, index: number) => ({
            text: {
              ro: item?.text?.ro,
              ru: item?.text?.ru
            },
            description: {
              ro: item?.text?.ro,
              ru: item?.text?.ru
            },
            type: item?.type,
            hasOther: item?.hasOther,
            order: index + 1,
            answerChoices: item?.answerChoices?.map(
              (answerChoice: UpdateAnswerChoiceDto, index: number) => ({
                text: {
                  ro: answerChoice?.text?.ro,
                  ru: answerChoice?.text?.ru
                },
                description: {
                  ro: answerChoice?.text?.ro,
                  ru: answerChoice?.text?.ru
                },
                mediaFileId: answerChoice?.mediaFileId,
                order: index + 1
              })
            )
          })
        )
      }
    })
  }

  if (isLoading) {
    return <LoadingScreen />
  }

  return (
    <PageBreadcrumbs
      title={t('questionnaires.edit_questionnaire')}
      items={[{ title: t('menu.questionnaires') }]}
    >
      <Flex vertical gap={24}>
        <Flex gap={16} justify="space-between">
          <Button
            icon={<LeftOutlined />}
            onClick={() =>
              isPreview ? onToggle() : navigate(paths.questionnaires.root)
            }
          >
            {t('common.back')}
          </Button>
          <Flex gap={8}>
            {type === QuestionnaireEnum.questions && (
              <Button onClick={onToggle}>
                {isPreview
                  ? t('questionnaires.back_to_edit')
                  : t('questionnaires.preview')}
              </Button>
            )}
            <Button onClick={() => navigate(paths.questionnaires.root)}>
              {t('common.cancel')}
            </Button>
            {isDraft && (
              <Button form="questions" type="primary" htmlType="submit">
                {t('common.save')}
              </Button>
            )}
          </Flex>
        </Flex>

        <Segmented
          className={styles.segmented}
          value={type}
          options={segmentedItems}
          onChange={(val) => onTabChange(val)}
        />

        <Form
          form={form}
          requiredMark={false}
          layout="vertical"
          name="questions"
          onSubmitCapture={onSubmit}
          disabled={data?.state === QuestionnaireState.ACTIVE && !isPreview}
        >
          {isPreview ? (
            <Card>
              <Preview form={form} />
            </Card>
          ) : (
            segmentedItems.find((item) => item.value === type)?.children
          )}
        </Form>
      </Flex>
    </PageBreadcrumbs>
  )
}
